/**
 * StepUpModalHost — global step-up overlay + mutation replay (FF-UI-2).
 *
 * Mounted once in the root layout. When the StepUpInterceptor enqueues a
 * STEP_UP_REQUIRED mutation failure, this host renders a modal OVER whatever
 * the user is doing (wizard, drawer, form — nothing unmounts), runs the
 * standard step-up sequence via useStepUpChallenge, then re-executes every
 * queued mutation with its original variables. The owning components observe
 * the same Mutation objects, so their isPending/onSuccess handlers (close
 * modal, toast, navigate) fire exactly as if the original click had succeeded.
 *
 * Cancel = dismiss the queue; mutations stay in their error state, which the
 * surfaces already render (wizard step-up notice, team-invite toast, etc.).
 *
 * Rendering detail: the host returns null until the queue opens, and the
 * inner modal remounts per open (key) so factor loading + OTP state start
 * fresh each time.
 *
 * z-index: above the standard Modal layer (8000/8001) so it stacks over
 * open wizards; see ui/modal.tsx zIndex prop.
 */

import { useState } from 'react';
import { Modal } from '../ui/modal';
import { useStepUpQueue, markReplayed } from '../../lib/api/step-up-store';
import { useStepUpChallenge } from '../../lib/use-step-up-challenge';
import { ApiError } from '../../lib/api/client';
import { useToastStore } from '../../lib/toast-store';

export function StepUpModalHost() {
  const open = useStepUpQueue((s) => s.open);
  if (!open) return null;
  return <StepUpModalInner />;
}

function StepUpModalInner() {
  const dismiss = useStepUpQueue((s) => s.dismiss);
  const pendingCount = useStepUpQueue((s) => s.pending.length);
  const {
    factor,
    showWebAuthnBranch,
    verifying,
    error,
    codeLabel,
    emailCodePending,
    notice,
    verifyTotp,
    verifyPasskey,
  } = useStepUpChallenge();
  const [otp, setOtp] = useState('');

  /** Replay everything queued, oldest first. Each mutation is marked BEFORE
   *  execute so a repeat STEP_UP_REQUIRED failure can't re-open the modal
   *  (the interceptor checks wasReplayed). Other failures land in the
   *  mutation's normal error state, rendered by its own surface. */
  async function replayQueue() {
    const pending = useStepUpQueue.getState().drain();
    for (const m of pending) {
      markReplayed(m);
      try {
        await m.execute(m.state.variables);
      } catch (err) {
        // COMBO-018: when a replay STILL fails STEP_UP_REQUIRED (clock skew, a
        // second 600s expiry mid-flight) the interceptor won't re-open the
        // modal AND the owning surface silently swallows isStepUpRequired
        // (it assumes a redirect is happening). That left the user with NO
        // feedback on a high-trust action they thought they'd confirmed —
        // critically ambiguous on a legally-binding resolution signature.
        // Surface it here, centrally, for EVERY step-up-gated mutation.
        if (err instanceof ApiError && err.isStepUpRequired) {
          useToastStore.getState().pushToast({
            kind: 'error',
            message:
              'Your security confirmation expired before the action completed. It was NOT recorded. Please try again.',
          });
        }
        // Other failures land in the mutation's own error state, rendered by
        // its owning surface. Never block the rest of the queue.
      }
    }
  }

  async function onOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    // While the extra email code is outstanding this same field carries it (see
    // use-step-up-challenge.ts). Clear the box after that step so the user is
    // not typing their authenticator code on top of the emailed one.
    const wasEmailStep = emailCodePending;
    const ok = await verifyTotp(otp);
    if (wasEmailStep) setOtp('');
    if (ok) await replayQueue();
  }

  async function onPasskeySign() {
    const ok = await verifyPasskey();
    if (ok) await replayQueue();
  }

  return (
    <Modal
      open
      onClose={dismiss}
      title="Confirm it’s you"
      size="sm"
      zIndex={8200}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 4 }}>
        <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0 }}>
          {pendingCount > 1
            ? 'These actions need a fresh MFA challenge. '
            : 'This action needs a fresh MFA challenge. '}
          Your session is valid; this is an additional confirmation for
          high-trust actions. Your work below is preserved. Verifying will
          retry {pendingCount > 1 ? 'them' : 'it'} automatically.
        </p>

        {/* Disclosure when the extra email code could not be delivered and was
            therefore skipped. Never silent — a control that quietly stopped
            applying is worse than one that never existed. */}
        {notice && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--hairline)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 13,
              color: 'var(--ink-2)',
            }}
          >
            {notice}
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--hairline)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 13,
              color: 'var(--ink-2)',
            }}
          >
            {error}
          </div>
        )}

        {showWebAuthnBranch ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, margin: 0 }}>
              Use your registered passkey (Face ID, Touch ID, Windows Hello, or
              security key) to confirm this action.
            </p>
            <div style={{ display: 'flex', gap: 10, paddingBottom: 20 }}>
              <button
                type="button"
                onClick={() => void onPasskeySign()}
                disabled={verifying}
                style={{
                  flex: 1,
                  padding: '12px 18px',
                  borderRadius: 10,
                  border: 'none',
                  background: verifying ? 'var(--surface-2)' : 'var(--accent-400)',
                  color: verifying ? 'var(--ink-4)' : 'var(--primary-900)',
                  fontFamily: 'var(--sans)',
                  fontSize: 14.5,
                  fontWeight: 600,
                  cursor: verifying ? 'not-allowed' : 'pointer',
                }}
              >
                {verifying ? 'Verifying…' : 'Sign with passkey'}
              </button>
              <button
                type="button"
                onClick={dismiss}
                style={{
                  padding: '12px 18px',
                  borderRadius: 10,
                  border: '1px solid var(--hairline)',
                  background: 'transparent',
                  color: 'var(--ink-2)',
                  fontFamily: 'var(--sans)',
                  fontSize: 14.5,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => void onOtpSubmit(e)}
            style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 20 }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>
                {codeLabel}
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000 000"
                autoFocus
                autoComplete="one-time-code"
                aria-label="One-time password"
                className="mono"
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: 9,
                  border: '1px solid var(--accent-300)',
                  background: 'var(--accent-50)',
                  color: 'var(--ink)',
                  fontFamily: 'var(--mono)',
                  fontSize: 28,
                  letterSpacing: '0.25em',
                  textAlign: 'center',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="submit"
                // `!factor` must NOT block the email step: a user whose second
                // factor is a passkey has no TOTP factor at all, and still has
                // to be able to submit their emailed code.
                disabled={otp.length !== 6 || verifying || (!factor && !emailCodePending)}
                style={{
                  flex: 1,
                  padding: '12px 18px',
                  borderRadius: 10,
                  border: 'none',
                  background:
                    otp.length === 6 && !verifying ? 'var(--accent-400)' : 'var(--surface-2)',
                  color: otp.length === 6 && !verifying ? 'var(--primary-900)' : 'var(--ink-4)',
                  fontFamily: 'var(--sans)',
                  fontSize: 14.5,
                  fontWeight: 600,
                  cursor: otp.length === 6 && !verifying ? 'pointer' : 'not-allowed',
                }}
              >
                {verifying ? 'Verifying…' : 'Confirm and retry'}
              </button>
              <button
                type="button"
                onClick={dismiss}
                style={{
                  padding: '12px 18px',
                  borderRadius: 10,
                  border: '1px solid var(--hairline)',
                  background: 'transparent',
                  color: 'var(--ink-2)',
                  fontFamily: 'var(--sans)',
                  fontSize: 14.5,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>

            {/* Not during the email step — a passkey-only user legitimately has
                no TOTP factor and must not be told to go and enrol one. */}
            {!factor && !emailCodePending && (
              <p style={{ fontSize: 12.5, color: 'var(--ink-4)', margin: 0, lineHeight: 1.55 }}>
                No MFA factor is enrolled on this account.{' '}
                <a href="/auth/mfa-enroll" style={{ color: 'var(--primary-700)' }}>
                  Enrol an authenticator
                </a>{' '}
                before continuing.
              </p>
            )}
          </form>
        )}
      </div>
    </Modal>
  );
}
